/**
 * AdminAssociations.jsx — CEO 2026-10-03
 *
 * CRUD complet des associations partenaires. Permet au CEO d'ajouter une
 * nouvelle asso (ex. Plan International) sans redéploy et de voir
 * immédiatement les champs utilisés dans le badge PDF (couleurs,
 * contacts donateurs, préfixe code RD).
 *
 * Pattern UI :
 *   • Liste en grille 2 colonnes (card par asso) avec preview couleurs
 *   • Bouton flottant « Nouvelle association » → modal création
 *   • Click sur une carte → modal édition
 *   • Dans la modal : upload logo recto + verso + color pickers + champs
 *     service donateurs (service, phone, email, address)
 *   • Soft-delete par défaut (toggle « Archivée » dans la modal) pour
 *     préserver l'historique des badges déjà rendus.
 */
import { motion } from "framer-motion";
import {
    AlertCircle,
    Building2,
    CheckCircle2,
    Loader2,
    Pencil,
    Plus,
    Save,
    Upload,
    X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";


export default function AdminAssociations() {
    const [assocs, setAssocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const [editing, setEditing] = useState(null); // null = closed, {} = create, {slug: ...} = edit

    async function reload() {
        setLoading(true);
        try {
            const r = await api.get("/associations", {
                params: { include_inactive: showInactive },
            });
            setAssocs(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [showInactive]);

    return (
        <div className="space-y-6" data-testid="admin-associations-page">
            <motion.header
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between gap-3 flex-wrap"
            >
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-shariti-black flex items-center gap-2">
                        <Building2 className="h-7 w-7 text-shariti-orange" />
                        Associations partenaires
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500 max-w-2xl">
                        Ces associations alimentent le dropdown « Association d'affectation » sur les fiches candidat et permanents, et déterminent la mise en page du badge PDF (logo, couleurs, coordonnées du service donateurs au verso).
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs font-bold cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            data-testid="assoc-show-inactive"
                        />
                        Afficher les archivées
                    </label>
                    <button
                        onClick={() => setEditing({})}
                        data-testid="assoc-new-btn"
                        className="btn-shariti"
                    >
                        <Plus className="h-4 w-4" />
                        Nouvelle association
                    </button>
                </div>
            </motion.header>

            {loading ? (
                <div className="py-24 text-center text-slate-400">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </div>
            ) : assocs.length === 0 ? (
                <div className="card-soft p-10 text-center text-slate-400">
                    Aucune association pour l'instant. Clique sur « Nouvelle association » pour commencer.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="assoc-list">
                    {assocs.map((a) => (
                        <AssociationCard
                            key={a.slug}
                            assoc={a}
                            onEdit={() => setEditing(a)}
                        />
                    ))}
                </div>
            )}

            {editing !== null && (
                <AssociationModal
                    assoc={editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        setEditing(null);
                        await reload();
                    }}
                />
            )}
        </div>
    );
}


function AssociationCard({ assoc, onEdit }) {
    const inactive = assoc.active === false;
    return (
        <motion.button
            type="button"
            onClick={onEdit}
            data-testid={`assoc-card-${assoc.slug}`}
            whileHover={{ y: -2 }}
            className={`card-soft p-4 text-left relative transition ${
                inactive ? "opacity-50 grayscale" : ""
            }`}
        >
            <div className="flex items-start gap-3 mb-3">
                {/* Mini preview badge couleur */}
                <div
                    className="h-14 w-14 rounded-2xl shrink-0 flex items-center justify-center border border-black/10"
                    style={{
                        backgroundColor: assoc.bg_color || "#F5F5F5",
                        color: assoc.text_color || "#000",
                    }}
                >
                    <span className="font-black text-xs text-center leading-tight">
                        {(assoc.code_prefix || "?").toUpperCase()}
                    </span>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-extrabold text-shariti-black truncate">
                        {assoc.name}
                        {inactive && (
                            <span className="ml-2 text-[10px] font-bold uppercase text-slate-400">
                                archivée
                            </span>
                        )}
                    </h3>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 truncate">
                        {assoc.slug}
                    </p>
                </div>
                <Pencil className="h-4 w-4 text-slate-300 shrink-0 group-hover:text-shariti-orange" />
            </div>
            <dl className="text-[11px] space-y-1 text-slate-600">
                <div className="flex gap-2">
                    <dt className="font-bold text-slate-400 w-20 shrink-0">Service</dt>
                    <dd className="truncate">{assoc.service || "—"}</dd>
                </div>
                <div className="flex gap-2">
                    <dt className="font-bold text-slate-400 w-20 shrink-0">Tél. donateurs</dt>
                    <dd>{assoc.phone || "—"}</dd>
                </div>
                <div className="flex gap-2">
                    <dt className="font-bold text-slate-400 w-20 shrink-0">Email</dt>
                    <dd className="truncate">{assoc.email || "—"}</dd>
                </div>
                <div className="flex gap-2">
                    <dt className="font-bold text-slate-400 w-20 shrink-0">Adresse</dt>
                    <dd className="truncate">{assoc.address || "—"}</dd>
                </div>
            </dl>
        </motion.button>
    );
}


function AssociationModal({ assoc, onClose, onSaved }) {
    const isCreate = !assoc.slug;
    const [form, setForm] = useState({
        name: assoc.name || "",
        code_prefix: assoc.code_prefix || "",
        service: assoc.service || "",
        phone: assoc.phone || "",
        email: assoc.email || "",
        address: assoc.address || "",
        bg_color: assoc.bg_color || "#F5F5F5",
        bg_color_verso: assoc.bg_color_verso || "#F5F5F5",
        text_color: assoc.text_color || "#000000",
        text_color_verso: assoc.text_color_verso || "#000000",
        strip_bg: assoc.strip_bg || "#FFFFFF",
        strip_text: assoc.strip_text || "#000000",
        active: assoc.active !== false,
    });
    const [saving, setSaving] = useState(false);
    const [uploadingSide, setUploadingSide] = useState(null); // 'recto' | 'verso' | null
    const [logoReloadKey, setLogoReloadKey] = useState(0);
    const rectoRef = useRef(null);
    const versoRef = useRef(null);

    function setField(k, v) { setForm((prev) => ({ ...prev, [k]: v })); }

    async function handleSave() {
        if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
        setSaving(true);
        try {
            if (isCreate) {
                const r = await api.post("/associations", form);
                toast.success(`Association « ${r.data.name} » créée`);
                // Après création, reste dans la modal pour permettre
                // l'upload des logos sur le nouveau slug.
                await onSaved();
            } else {
                await api.patch(`/associations/${assoc.slug}`, form);
                toast.success("Association mise à jour");
                await onSaved();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur à l'enregistrement");
        } finally {
            setSaving(false);
        }
    }

    async function handleLogoUpload(side, file) {
        if (!file || isCreate) {
            if (isCreate) toast.info("Enregistre d'abord l'association pour pouvoir uploader un logo.");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Logo trop lourd (max 2 Mo)");
            return;
        }
        setUploadingSide(side);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/associations/${assoc.slug}/logo?side=${side}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Logo ${side} mis à jour`);
            setLogoReloadKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload échoué");
        } finally {
            setUploadingSide(null);
        }
    }

    async function handleArchive() {
        if (!window.confirm(`Archiver ${form.name} ? Les badges déjà imprimés restent intacts, mais la dropdown ne la proposera plus.`)) return;
        try {
            await api.delete(`/associations/${assoc.slug}`);
            toast.success("Association archivée");
            await onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur archivage");
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto p-4"
            data-testid="assoc-modal"
        >
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl w-full max-w-3xl my-8 shadow-xl"
            >
                <header className="flex items-center justify-between gap-3 px-6 py-4 border-b border-shariti-gray">
                    <h2 className="text-xl font-extrabold">
                        {isCreate ? "Nouvelle association" : `Éditer ${assoc.name}`}
                    </h2>
                    <button
                        onClick={onClose}
                        data-testid="assoc-modal-close"
                        className="h-8 w-8 rounded-full hover:bg-shariti-cream flex items-center justify-center"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
                    {/* Identité */}
                    <section className="space-y-3">
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Identité
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <TextField
                                label="Nom affiché"
                                value={form.name}
                                onChange={(v) => setField("name", v)}
                                placeholder="Ex. Plan International France"
                                testid="assoc-form-name"
                                className="md:col-span-2"
                                required
                            />
                            <TextField
                                label="Préfixe code RD"
                                value={form.code_prefix}
                                onChange={(v) => setField("code_prefix", v.toUpperCase().slice(0, 4))}
                                placeholder="PI"
                                testid="assoc-form-prefix"
                            />
                        </div>
                    </section>

                    {/* Service donateurs — infos VERSO du badge */}
                    <section className="space-y-3">
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Service relations donateurs (affiché au verso du badge)
                        </h3>
                        <TextField
                            label="Intitulé du service"
                            value={form.service}
                            onChange={(v) => setField("service", v)}
                            placeholder="Service Relations Donateurs"
                            testid="assoc-form-service"
                        />
                        <TextField
                            label="Adresse postale"
                            value={form.address}
                            onChange={(v) => setField("address", v)}
                            placeholder="14 Rue Scandicci, Tour Essor, 93500 Pantin"
                            testid="assoc-form-address"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <TextField
                                label="Téléphone"
                                value={form.phone}
                                onChange={(v) => setField("phone", v)}
                                placeholder="01 84 87 03 50"
                                testid="assoc-form-phone"
                            />
                            <TextField
                                label="Email"
                                value={form.email}
                                onChange={(v) => setField("email", v)}
                                placeholder="contact@plan-international.fr"
                                testid="assoc-form-email"
                                type="email"
                            />
                        </div>
                    </section>

                    {/* Couleurs — mise en page badge */}
                    <section className="space-y-3">
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Couleurs du badge
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <ColorField label="Fond recto" value={form.bg_color} onChange={(v) => setField("bg_color", v)} testid="assoc-form-bg-color" />
                            <ColorField label="Texte recto" value={form.text_color} onChange={(v) => setField("text_color", v)} testid="assoc-form-text-color" />
                            <ColorField label="Bande rouge RD" value={form.strip_bg} onChange={(v) => setField("strip_bg", v)} testid="assoc-form-strip-bg" />
                            <ColorField label="Fond verso" value={form.bg_color_verso} onChange={(v) => setField("bg_color_verso", v)} testid="assoc-form-bg-verso" />
                            <ColorField label="Texte verso" value={form.text_color_verso} onChange={(v) => setField("text_color_verso", v)} testid="assoc-form-text-verso" />
                            <ColorField label="Texte bande" value={form.strip_text} onChange={(v) => setField("strip_text", v)} testid="assoc-form-strip-text" />
                        </div>
                        {/* Preview visuelle */}
                        <div className="rounded-2xl border border-shariti-gray p-3 flex items-center justify-center gap-4">
                            <div
                                className="w-32 h-20 rounded-xl flex items-center justify-center text-xs font-black"
                                style={{ backgroundColor: form.bg_color, color: form.text_color }}
                            >
                                Recto
                            </div>
                            <div
                                className="w-32 h-20 rounded-xl flex items-center justify-center text-xs font-black"
                                style={{ backgroundColor: form.bg_color_verso, color: form.text_color_verso }}
                            >
                                Verso
                            </div>
                            <div
                                className="w-32 h-6 rounded-md flex items-center justify-center text-[10px] font-black"
                                style={{ backgroundColor: form.strip_bg, color: form.strip_text }}
                            >
                                Bande code RD
                            </div>
                        </div>
                    </section>

                    {/* Logos */}
                    <section className="space-y-3">
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                            Logos
                        </h3>
                        {isCreate && (
                            <p className="text-[11px] font-bold text-shariti-orange bg-shariti-orange-soft/50 rounded-lg px-3 py-2 flex items-center gap-2">
                                <AlertCircle className="h-3.5 w-3.5" />
                                Enregistre d'abord l'association ci-dessous — tu pourras ensuite revenir ici pour uploader les logos.
                            </p>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <LogoUpload
                                side="recto"
                                assoc={assoc}
                                reloadKey={logoReloadKey}
                                uploading={uploadingSide === "recto"}
                                disabled={isCreate}
                                onPick={() => rectoRef.current?.click()}
                            />
                            <LogoUpload
                                side="verso"
                                assoc={assoc}
                                reloadKey={logoReloadKey}
                                uploading={uploadingSide === "verso"}
                                disabled={isCreate}
                                onPick={() => versoRef.current?.click()}
                            />
                        </div>
                        <input
                            type="file"
                            ref={rectoRef}
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => handleLogoUpload("recto", e.target.files?.[0])}
                            data-testid="assoc-form-upload-recto"
                        />
                        <input
                            type="file"
                            ref={versoRef}
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={(e) => handleLogoUpload("verso", e.target.files?.[0])}
                            data-testid="assoc-form-upload-verso"
                        />
                    </section>

                    {/* Toggle actif / archivé */}
                    {!isCreate && (
                        <section>
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={(e) => setField("active", e.target.checked)}
                                    data-testid="assoc-form-active"
                                />
                                <span className="text-sm font-bold">Association active (visible dans les dropdowns)</span>
                            </label>
                        </section>
                    )}
                </div>

                <footer className="flex items-center justify-between gap-3 px-6 py-4 border-t border-shariti-gray flex-wrap">
                    {!isCreate ? (
                        <button
                            onClick={handleArchive}
                            data-testid="assoc-form-archive"
                            className="text-xs font-bold text-shariti-red hover:underline"
                        >
                            Archiver cette association
                        </button>
                    ) : <div />}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="btn-ghost"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            data-testid="assoc-form-save"
                            className="btn-shariti disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {isCreate ? "Créer" : "Enregistrer"}
                        </button>
                    </div>
                </footer>
            </motion.div>
        </div>
    );
}


function TextField({ label, value, onChange, placeholder, testid, type = "text", className = "", required = false }) {
    return (
        <label className={`block ${className}`}>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">
                {label} {required && <span className="text-shariti-red">*</span>}
            </span>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="input-shariti"
            />
        </label>
    );
}


function ColorField({ label, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">
                {label}
            </span>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={value || "#000000"}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="h-9 w-12 rounded-xl border border-shariti-gray cursor-pointer"
                />
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="#000000"
                    className="input-shariti font-mono text-xs flex-1"
                />
            </div>
        </label>
    );
}


function LogoUpload({ side, assoc, reloadKey, uploading, disabled, onPick }) {
    const [logo, setLogo] = useState(null);

    useEffect(() => {
        if (!assoc.slug) { setLogo(null); return; }
        api.get(`/associations/${assoc.slug}`).then((r) => {
            setLogo(r.data?.[`logo_${side}_b64`] || null);
        }).catch(() => setLogo(null));
    }, [assoc.slug, side, reloadKey]);

    return (
        <div className="rounded-2xl border-2 border-dashed border-shariti-gray p-4 text-center space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Logo {side}
            </p>
            {logo ? (
                <img src={logo} alt={`Logo ${side}`} className="max-h-20 mx-auto object-contain" />
            ) : (
                <div className="h-20 flex items-center justify-center text-[11px] text-slate-300">
                    (aucun logo custom · fallback filesystem)
                </div>
            )}
            <button
                type="button"
                onClick={onPick}
                disabled={disabled || uploading}
                data-testid={`assoc-logo-${side}-btn`}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-shariti-orange hover:text-shariti-orange/80 disabled:opacity-40"
            >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploading ? "Upload…" : (logo ? "Remplacer" : "Uploader")}
            </button>
        </div>
    );
}


// eslint-disable-next-line no-unused-vars
const _ = CheckCircle2;
